// Filtros de estado de la liquidación a repartidores.
//
// Son filtros de CLIENTE sobre lo que ya devuelve `GET /billing/driver-payroll/summary`:
// los tres campos que se filtran (`vehicleType`, `billingDocType`, `billingDocStatus`)
// ya vienen en el payload, así que no hace falta ningún endpoint nuevo.
//
// ALCANCE — importante: la lista de repartidores del summary se arma desde los paquetes
// entregados y los días con extras del rango (getOperatorPayroll). Es decir, la vista
// solo conoce repartidores CON ACTIVIDAD en el período. Un repartidor sin vehículo que
// no entregó nada esa semana no aparece acá. La UI lo dice explícitamente en vez de
// dejar creer que el filtro barre todo el plantel.
//
// Los filtros son EXCLUYENTES (uno a la vez). Combinarlos daría cruces contradictorios
// —"sin documento elegido" + "documento subido"— que devuelven cero filas sin que el
// operador entienda por qué.

/** Valor del filtro "sin filtrar". */
export const DRIVER_FILTER_ALL = '';

/**
 * Definición de cada filtro.
 *
 * `field` es el campo del que depende: si NINGÚN repartidor del payload lo trae
 * definido, el backend es viejo y el chip se oculta. Sin esa guarda, un backend sin
 * `billingDocStatus` haría que "falta su factura/boleta" marcara a todo el mundo, y un
 * `vehicleType` ausente reportaría a todos como "sin vehículo": inventaríamos un
 * problema que no sabemos si existe.
 *
 * `group` agrupa los chips en la barra ('pago' / 'documento' / 'ficha'): la pregunta
 * del operador es distinta en cada grupo ("¿a quién le falta plata?" vs "¿qué documento
 * falta validar?" vs "¿qué ficha está incompleta?") y una tira plana de 8 chips las
 * mezclaba todas.
 *
 * A los freelancers no se les exige documento tributario (no se les paga por planilla),
 * así que quedan fuera de los filtros de documento. De los filtros de PAGO no queda
 * fuera nadie: freelancer o no, sus días se marcan pagados igual.
 */
export const DRIVER_FILTERS = [
  {
    value: DRIVER_FILTER_ALL,
    label: 'Todos',
    hint: 'Todos los repartidores con actividad en el período.',
    group: null,
    field: null,
    match: null,
  },
  {
    value: 'pay-pending',
    label: 'Pago pendiente',
    hint: 'Tiene días del período aún sin pagar.',
    group: 'pago',
    field: 'paidDaysCount',
    match: (d) => (d.daysCount ?? 0) > (d.paidDaysCount ?? 0),
  },
  {
    value: 'pay-complete',
    label: 'Pagado',
    hint: 'Todos sus días del período ya están pagados.',
    group: 'pago',
    field: 'paidDaysCount',
    match: (d) => (d.daysCount ?? 0) > 0 && (d.paidDaysCount ?? 0) >= d.daysCount,
  },
  {
    value: 'doc-uploaded',
    label: 'Documento por validar',
    hint: 'Subió su factura o boleta desde la app y espera que la aceptes o rechaces.',
    group: 'documento',
    field: 'billingDocStatus',
    match: (d) => !d.isFreelancer && d.billingDocStatus === 'uploaded',
  },
  {
    value: 'doc-missing',
    label: 'Falta su documento',
    hint: 'Todavía no sube su factura o boleta de las semanas del período.',
    group: 'documento',
    field: 'billingDocStatus',
    match: (d) => !d.isFreelancer && d.billingDocStatus === 'missing',
  },
  {
    value: 'doc-rejected',
    label: 'Documento rechazado',
    hint: 'Su documento fue rechazado y debe subir otro.',
    group: 'documento',
    field: 'billingDocStatus',
    match: (d) => !d.isFreelancer && d.billingDocStatus === 'rejected',
  },
  {
    value: 'doc-accepted',
    label: 'Documento aceptado',
    hint: 'Su factura o boleta ya fue aceptada por el operador.',
    group: 'documento',
    field: 'billingDocStatus',
    match: (d) => !d.isFreelancer && d.billingDocStatus === 'accepted',
  },
  {
    value: 'no-doc-type',
    label: 'Sin documento elegido',
    hint: 'No eligió factura ni boleta. Queda fuera de la planilla del banco hasta que lo haga.',
    group: 'ficha',
    field: 'billingDocType',
    match: (d) => !d.isFreelancer && d.billingDocType == null,
  },
  {
    value: 'no-vehicle',
    label: 'Sin vehículo',
    hint: 'No tiene tipo de vehículo configurado en su ficha.',
    group: 'ficha',
    field: 'vehicleType',
    match: (d) => !d.vehicleType,
  },
];

/** Rótulos de los grupos de chips, en el orden en que se pintan. */
export const DRIVER_FILTER_GROUPS = [
  { key: 'pago', label: 'Pago' },
  { key: 'documento', label: 'Documento' },
  { key: 'ficha', label: 'Ficha' },
];

/** Filtros que el payload actual puede sostener (ver `field` arriba). */
export const availableDriverFilters = (drivers = []) =>
  DRIVER_FILTERS.filter(
    (f) => !f.field || drivers.some((d) => d[f.field] !== undefined),
  );

/** Aplica un filtro. Un valor desconocido no filtra (nunca esconde datos en silencio). */
export const applyDriverFilter = (drivers = [], value) => {
  const f = DRIVER_FILTERS.find((x) => x.value === value);
  return f && f.match ? drivers.filter(f.match) : drivers;
};

/**
 * Contador por filtro, calculado sobre el período COMPLETO (no sobre el filtro activo):
 * si el contador cambiara al filtrar, el chip dejaría de servir para navegar.
 */
export const driverFilterCounts = (drivers = []) => {
  const counts = {};
  for (const f of DRIVER_FILTERS) {
    counts[f.value] = f.match ? drivers.filter(f.match).length : drivers.length;
  }
  return counts;
};

/**
 * Monto que FALTA pagarle a un repartidor: Σ del total de sus días 'unpaid'. Los días
 * 'partial' ya están pagados (paid=true con paquetes retenidos; lo retenido se libera
 * por otro flujo), así que no cuentan como pendiente.
 */
export const driverPendingAmount = (d) =>
  (d?.days ?? []).reduce(
    (sum, day) => sum + (day.status === 'unpaid' ? (day.totalPayable ?? 0) : 0),
    0,
  );

/**
 * Pendiente del período completo: monto total sin pagar y cuántos repartidores lo
 * componen. Alimenta la cifra "Pendiente por pagar" de la cabecera de totales.
 */
export const pendingSummary = (drivers = []) => {
  let amount = 0;
  let count = 0;
  for (const d of drivers) {
    const p = driverPendingAmount(d);
    if (p > 0) {
      amount += p;
      count += 1;
    }
  }
  return { amount, drivers: count };
};
